import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { productsAPI, adminAPI } from '../services/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Package } from 'lucide-react';

const AdminPanel = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    loadData();
  }, [isAdmin, activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'products') {
        const data = await productsAPI.getAll();
        setProducts(data);
      } else if (activeTab === 'orders') {
        const data = await adminAPI.getAllOrders();
        setOrders(data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await productsAPI.delete(id);
      toast.success('Product deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await adminAPI.updateOrderStatus(orderId, status);
      toast.success('Order status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-white pt-20">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12">
        <h1 className="text-4xl md:text-6xl font-black tracking-tighter mb-12">
          ADMIN PANEL
        </h1>

        {/* Tabs */}
        <div className="flex gap-6 mb-12 border-b-2 border-black">
          <button
            onClick={() => setActiveTab('products')}
            className={`text-sm font-bold tracking-wider pb-4 border-b-2 transition-colors ${
              activeTab === 'products'
                ? 'border-black'
                : 'border-transparent hover:border-gray-300'
            }`}
          >
            PRODUCTS
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`text-sm font-bold tracking-wider pb-4 border-b-2 transition-colors ${
              activeTab === 'orders'
                ? 'border-black'
                : 'border-transparent hover:border-gray-300'
            }`}
          >
            ORDERS
          </button>
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <button
              onClick={() => navigate('/admin/product/new')}
              className="bg-black text-white px-6 py-3 text-sm font-bold tracking-wider hover:bg-gray-800 transition-colors mb-8 inline-flex items-center gap-2"
            >
              <Plus size={18} strokeWidth={2.5} />
              ADD PRODUCT
            </button>

            {loading ? (
              <p>Loading...</p>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {products.map((product) => (
                  <div
                    key={product._id}
                    className="border-2 border-black p-6 flex justify-between items-center"
                  >
                    <div className="flex gap-6 items-center flex-1">
                      <div
                        className="w-20 h-24"
                        style={{ backgroundColor: product.colorBlock }}
                      />
                      <div>
                        <h3 className="text-lg font-bold tracking-wider">
                          {product.name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {product.category} • ${product.price}
                        </p>
                        <p className="text-sm">
                          {product.inStock ? (
                            <span className="text-green-600 font-bold">IN STOCK</span>
                          ) : (
                            <span className="text-red-600 font-bold">OUT OF STOCK</span>
                          )}
                          {product.featured && (
                            <span className="ml-4 text-black font-bold">★ FEATURED</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => navigate(`/admin/product/${product._id}`)}
                        className="border-2 border-black p-3 hover:bg-black hover:text-white transition-colors"
                      >
                        <Edit size={18} strokeWidth={2.5} />
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product._id)}
                        className="border-2 border-black p-3 hover:bg-black hover:text-white transition-colors"
                      >
                        <Trash2 size={18} strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            {loading ? (
              <p>Loading...</p>
            ) : orders.length === 0 ? (
              <p className="text-gray-400 text-center py-12">No orders yet</p>
            ) : (
              <div className="space-y-6">
                {orders.map((order) => (
                  <div key={order._id} className="border-2 border-black p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-bold tracking-wider">
                          Order #{order._id.slice(-6).toUpperCase()}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-sm">
                          {order.shipping_info.firstName} {order.shipping_info.lastName}
                        </p>
                        <p className="text-sm text-gray-600">
                          {order.shipping_info.email}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">${order.total}</p>
                        <select
                          value={order.status}
                          onChange={(e) =>
                            handleUpdateOrderStatus(order._id, e.target.value)
                          }
                          className="mt-2 border-2 border-black px-4 py-2 text-sm font-bold bg-white"
                        >
                          <option value="pending">PENDING</option>
                          <option value="processing">PROCESSING</option>
                          <option value="shipped">SHIPPED</option>
                          <option value="delivered">DELIVERED</option>
                          <option value="cancelled">CANCELLED</option>
                        </select>
                      </div>
                    </div>
                    <div className="border-t-2 border-gray-200 pt-4">
                      <p className="text-sm font-bold mb-2">ITEMS:</p>
                      {order.items.map((item, idx) => (
                        <p key={idx} className="text-sm text-gray-600">
                          {item.name} ({item.selectedSize}) x{item.quantity} - $
                          {item.item_total}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
